import React, { useCallback, useContext, useEffect, useMemo } from "react";
import type { Geometry } from "geojson";
import { LayerType } from "src/common/managers/Types";
import type { ANNAnnotationRecord, SaveAnnotationInput } from "./ANNTypes";
import { AnnotationContextType } from "./ANNTypes";
import { ANNManager } from "./ANNManager";
export interface AnnotationLayerCandidate {
   layerId: number;
   layerName: string;
   datasetName?: string;
   layerType: LayerType;
   dataUrl?: string;
   coordinates?: [number, number][];
   locked?: boolean;
   geoJsonGeometries?: Geometry[];
}
export interface ANNState {
   isInitialized: boolean;
   currentContextType?: AnnotationContextType;
   currentIdentifier?: string | number;
   currentOrganizationId?: string;
   recordsById: Record<string, ANNAnnotationRecord>;
   scopes: Record<string, ANNScopeState>;
   annotations: ANNAnnotationRecord[];
   loading: boolean;
   saving: boolean;
   error?: string;
   annLayers: AnnotationLayerCandidate[];
}
export type ANNScopedListKind = "scoped" | "all";
export interface ANNScopedListRequest {
   kind?: ANNScopedListKind;
   contextType: AnnotationContextType;
   identifier?: string | number;
   organizationId?: string;
   filterGeometry?: Geometry;
   allOrganizations?: boolean;
}
export interface ANNScopeState {
   request: ANNScopedListRequest;
   ids: string[];
   loading: boolean;
   initialized: boolean;
   error?: string;
}
export const initialANNState = (): ANNState => ({
   isInitialized: false,
   currentContextType: undefined,
   currentIdentifier: undefined,
   currentOrganizationId: undefined,
   recordsById: {},
   scopes: {},
   annotations: [],
   loading: false,
   saving: false,
   error: undefined,
   annLayers: [],
});
export enum ANNActionType {
   LoadAnnotationsRequest = "LoadAnnotationsRequest",
   LoadAnnotationsSuccess = "LoadAnnotationsSuccess",
   LoadAnnotationsFailure = "LoadAnnotationsFailure",
   SaveAnnotationRequest = "SaveAnnotationRequest",
   SaveAnnotationSuccess = "SaveAnnotationSuccess",
   SaveAnnotationFailure = "SaveAnnotationFailure",
   DeleteAnnotationSuccess = "DeleteAnnotationSuccess",
   SetAnnLayers = "SetAnnLayers",
}
export interface LoadAnnotationsRequestAction {
   type: ANNActionType.LoadAnnotationsRequest;
   payload: ANNScopedListRequest;
}
export interface LoadAnnotationsSuccessAction {
   type: ANNActionType.LoadAnnotationsSuccess;
   payload: ANNScopedListRequest & { annotations: ANNAnnotationRecord[] };
}
export interface LoadAnnotationsFailureAction {
   type: ANNActionType.LoadAnnotationsFailure;
   payload: ANNScopedListRequest & { error: string };
}
export interface SaveAnnotationRequestAction {
   type: ANNActionType.SaveAnnotationRequest;
}
export interface SaveAnnotationSuccessAction {
   type: ANNActionType.SaveAnnotationSuccess;
   payload: ANNAnnotationRecord;
}
export interface SaveAnnotationFailureAction {
   type: ANNActionType.SaveAnnotationFailure;
   payload: string;
}
export interface DeleteAnnotationSuccessAction {
   type: ANNActionType.DeleteAnnotationSuccess;
   payload: { id: string };
}
export interface SetAnnLayersAction {
   type: ANNActionType.SetAnnLayers;
   payload: AnnotationLayerCandidate[];
}
export type ANNAction =
   | LoadAnnotationsRequestAction
   | LoadAnnotationsSuccessAction
   | LoadAnnotationsFailureAction
   | SaveAnnotationRequestAction
   | SaveAnnotationSuccessAction
   | SaveAnnotationFailureAction
   | DeleteAnnotationSuccessAction
   | SetAnnLayersAction;
function stableGeometryKey(geometry?: Geometry): string {
   return geometry ? JSON.stringify(geometry) : "";
}
export function getANNListScopeKey(request: ANNScopedListRequest): string {
   const kind = request.kind || "scoped";
   const organizationId = request.allOrganizations ? "*" : (request.organizationId || "");
   const identifier = request.identifier === undefined || request.identifier === null ? "" : String(request.identifier);
   const geometryKey = kind === "all" ? stableGeometryKey(request.filterGeometry) : "";
   return [kind, organizationId, request.contextType, identifier, geometryKey].join("::");
}
function uniqueIds(records: ANNAnnotationRecord[]): string[] {
   const seen = new Set<string>();
   const ids: string[] = [];
   records.forEach(record => {
      if (!record.id || seen.has(record.id)) {
         return;
      }
      seen.add(record.id);
      ids.push(record.id);
   });
   return ids;
}
function matchesScopedRequest(record: ANNAnnotationRecord, request: ANNScopedListRequest): boolean {
   if (!request.allOrganizations && request.organizationId && record.organizationId !== request.organizationId) {
      return false;
   }
   if (record.contextType !== request.contextType) {
      return false;
   }
   if ((request.kind || "scoped") === "all") {
      // Geometry-filtered global scopes are loaded from ES and only reconcile records already present in the scope.
      return !request.filterGeometry;
   }
   if (request.identifier === undefined || request.identifier === null || request.identifier === "") {
      return false;
   }
   if (request.contextType === AnnotationContextType.Servitut) {
      return String(record.documentId || "") === String(request.identifier);
   }
   return String(record.bfeNumber || "") === String(request.identifier);
}
function sortAnnotations(records: ANNAnnotationRecord[]): ANNAnnotationRecord[] {
   return records.slice().sort((a, b) => {
      const aTouchedAt = a.lastEditedAt || a.createdAt || "";
      const bTouchedAt = b.lastEditedAt || b.createdAt || "";
      return new Date(bTouchedAt).getTime() - new Date(aTouchedAt).getTime();
   });
}
function upsertRecordIntoState(state: ANNState, record: ANNAnnotationRecord): ANNState {
   const recordsById = {
      ...state.recordsById,
      [record.id]: record,
   };
   const scopes = Object.fromEntries(
      Object.entries(state.scopes).map(([key, scope]) => {
         const hasRecord = scope.ids.includes(record.id);
         const canAdd = matchesScopedRequest(record, scope.request);
         const shouldKeep = hasRecord
            ? scope.request.filterGeometry
               ? record.contextType === scope.request.contextType && (!scope.request.organizationId || record.organizationId === scope.request.organizationId)
               : canAdd
            : canAdd;
         let ids = scope.ids;
         if (shouldKeep && !hasRecord) {
            ids = [record.id, ...scope.ids];
         } else if (!shouldKeep && hasRecord) {
            ids = scope.ids.filter(id => id !== record.id);
         }
         return [key, { ...scope, ids }];
      })
   );
   const annotations = sortAnnotations(Object.values(recordsById));
   return { ...state, recordsById, scopes, annotations };
}
function removeRecordFromState(state: ANNState, id: string): ANNState {
   const recordsById = { ...state.recordsById };
   delete recordsById[id];
   const scopes = Object.fromEntries(
      Object.entries(state.scopes).map(([key, scope]) => [
         key,
         { ...scope, ids: scope.ids.filter(scopeId => scopeId !== id) },
      ])
   );
   const annotations = state.annotations.filter(record => record.id !== id);
   return { ...state, recordsById, scopes, annotations };
}
export const annLoadAnnotationsRequest = (
   contextType?: AnnotationContextType,
   identifier?: string | number,
   organizationId?: string,
   kind: ANNScopedListKind = "scoped",
   filterGeometry?: Geometry,
   allOrganizations?: boolean
): LoadAnnotationsRequestAction => ({
   type: ANNActionType.LoadAnnotationsRequest,
   payload: { contextType: contextType || AnnotationContextType.Servitut, identifier, organizationId, kind, filterGeometry, allOrganizations },
});
export const annLoadAnnotationsSuccess = (
   contextType: AnnotationContextType | undefined,
   identifier: string | number | undefined,
   organizationId: string | undefined,
   annotations: ANNAnnotationRecord[],
   kind: ANNScopedListKind = "scoped",
   filterGeometry?: Geometry,
   allOrganizations?: boolean
): LoadAnnotationsSuccessAction => ({
   type: ANNActionType.LoadAnnotationsSuccess,
   payload: { contextType: contextType || AnnotationContextType.Servitut, identifier, organizationId, annotations, kind, filterGeometry, allOrganizations },
});
export const annLoadAnnotationsFailure = (
   contextType: AnnotationContextType | undefined,
   identifier: string | number | undefined,
   organizationId: string | undefined,
   error: string,
   kind: ANNScopedListKind = "scoped",
   filterGeometry?: Geometry,
   allOrganizations?: boolean
): LoadAnnotationsFailureAction => ({
   type: ANNActionType.LoadAnnotationsFailure,
   payload: { contextType: contextType || AnnotationContextType.Servitut, identifier, organizationId, error, kind, filterGeometry, allOrganizations },
});
export const annSaveAnnotationRequest = (): SaveAnnotationRequestAction => ({
   type: ANNActionType.SaveAnnotationRequest,
});
export const annSaveAnnotationSuccess = (annotation: ANNAnnotationRecord): SaveAnnotationSuccessAction => ({
   type: ANNActionType.SaveAnnotationSuccess,
   payload: annotation,
});
export const annSaveAnnotationFailure = (error: string): SaveAnnotationFailureAction => ({
   type: ANNActionType.SaveAnnotationFailure,
   payload: error,
});
export const annDeleteAnnotationSuccess = (id: string): DeleteAnnotationSuccessAction => ({
   type: ANNActionType.DeleteAnnotationSuccess,
   payload: { id },
});
export const annSetAnnLayers = (layers: AnnotationLayerCandidate[]): SetAnnLayersAction => ({
   type: ANNActionType.SetAnnLayers,
   payload: layers,
});
export const annReducer: React.Reducer<ANNState, ANNAction> = (state, action) => {
   switch (action.type) {
      case ANNActionType.LoadAnnotationsRequest:
         if (!action.payload.contextType) {
            return state;
         }
         const requestKey = getANNListScopeKey(action.payload);
         return {
            ...state,
            currentContextType: action.payload.contextType,
            currentIdentifier: action.payload.identifier,
            currentOrganizationId: action.payload.organizationId,
            scopes: {
               ...state.scopes,
               [requestKey]: {
                  request: action.payload,
                  ids: state.scopes[requestKey]?.ids || [],
                  loading: true,
                  initialized: state.scopes[requestKey]?.initialized || false,
                  error: undefined,
               },
            },
            loading: true,
            error: undefined,
         };
      case ANNActionType.LoadAnnotationsSuccess: {
         const { contextType, identifier, organizationId, annotations, kind, filterGeometry, allOrganizations } = action.payload;
         const request: ANNScopedListRequest = { contextType, identifier, organizationId, kind, filterGeometry, allOrganizations };
         const key = getANNListScopeKey(request);
         const recordsById = { ...state.recordsById };
         annotations.forEach(record => {
            recordsById[record.id] = record;
         });
         return {
            ...state,
            currentContextType: contextType,
            currentIdentifier: identifier,
            currentOrganizationId: organizationId,
            recordsById,
            scopes: {
               ...state.scopes,
               [key]: {
                  request,
                  ids: uniqueIds(annotations),
                  loading: false,
                  initialized: true,
                  error: undefined,
               },
            },
            annotations: sortAnnotations(Object.values(recordsById)),
            loading: false,
            isInitialized: true,
            error: undefined,
         };
      }
      case ANNActionType.LoadAnnotationsFailure: {
         const { contextType, identifier, organizationId, error, kind, filterGeometry, allOrganizations } = action.payload;
         const request: ANNScopedListRequest = { contextType, identifier, organizationId, kind, filterGeometry, allOrganizations };
         const key = getANNListScopeKey(request);
         return {
            ...state,
            scopes: {
               ...state.scopes,
               [key]: {
                  request,
                  ids: state.scopes[key]?.ids || [],
                  loading: false,
                  initialized: state.scopes[key]?.initialized || false,
                  error,
               },
            },
            loading: false,
            error,
         };
      }
      case ANNActionType.SaveAnnotationRequest:
         return { ...state, saving: true, error: undefined };
      case ANNActionType.SaveAnnotationSuccess: {
         const annotation = action.payload;
         return { ...upsertRecordIntoState(state, annotation), saving: false };
      }
      case ANNActionType.SaveAnnotationFailure:
         return { ...state, saving: false, error: action.payload };
      case ANNActionType.DeleteAnnotationSuccess:
         return removeRecordFromState({ ...state, saving: false }, action.payload.id);
      case ANNActionType.SetAnnLayers:
         return { ...state, annLayers: action.payload };
      default:
         return state;
   }
};
export type ANNDispatch = React.Dispatch<ANNAction>;
export const ANNContext = React.createContext<{
   state: ANNState;
   dispatch: ANNDispatch;
}>({
   state: initialANNState(),
   dispatch: () => undefined,
});
export function transactionalANNReducer(action: ANNAction, dispatch: ANNDispatch) {
   dispatch(action);
}
export function useANNContext() {
   return useContext(ANNContext);
}
export function selectANNAnnotations(state: ANNState, request: ANNScopedListRequest): ANNAnnotationRecord[] {
   const scope = state.scopes[getANNListScopeKey(request)];
   if (!scope) {
      return [];
   }
   return scope.ids
      .map(id => state.recordsById[id])
      .filter((record): record is ANNAnnotationRecord => Boolean(record));
}
export function useAnnotations(request: ANNScopedListRequest) {
   const { state, dispatch } = useANNContext();
   const scopeKey = getANNListScopeKey(request);
   const scope = state.scopes[scopeKey];
   const annotations = useMemo(() => selectANNAnnotations(state, request), [state, scopeKey]);
   const refresh = useCallback(async () => {
      if (request.contextType === undefined) {
         dispatch(annLoadAnnotationsSuccess(request.contextType, request.identifier, request.organizationId, [], request.kind || "scoped", request.filterGeometry, request.allOrganizations));
         return [];
      }
      if (!request.allOrganizations && !request.organizationId) {
         dispatch(annLoadAnnotationsSuccess(request.contextType, request.identifier, request.organizationId, [], request.kind || "scoped", request.filterGeometry, request.allOrganizations));
         return [];
      }
      if ((request.kind || "scoped") === "scoped" && (request.identifier === undefined || request.identifier === null || request.identifier === "")) {
         dispatch(annLoadAnnotationsSuccess(request.contextType, request.identifier, request.organizationId, [], request.kind || "scoped", request.filterGeometry, request.allOrganizations));
         return [];
      }
      dispatch(annLoadAnnotationsRequest(request.contextType, request.identifier, request.organizationId, request.kind || "scoped", request.filterGeometry, request.allOrganizations));
      try {
         const items = (request.kind || "scoped") === "all"
            ? await ANNManager.listAllAnnotations(request.contextType, request.organizationId, request.filterGeometry, { allOrganizations: request.allOrganizations })
            : await ANNManager.listAnnotations(request.contextType, request.identifier, request.organizationId);
         dispatch(annLoadAnnotationsSuccess(request.contextType, request.identifier, request.organizationId, items, request.kind || "scoped", request.filterGeometry, request.allOrganizations));
         return items;
      } catch (err: any) {
         dispatch(annLoadAnnotationsFailure(request.contextType, request.identifier, request.organizationId, err?.message || String(err), request.kind || "scoped", request.filterGeometry, request.allOrganizations));
         return [];
      }
   }, [dispatch, request.contextType, request.identifier, request.organizationId, request.kind, request.filterGeometry, request.allOrganizations]);
   useEffect(() => {
      if (!scope?.initialized && !scope?.loading) {
         void refresh();
      }
   }, [scope?.initialized, scope?.loading, refresh]);
   return {
      annotations,
      loading: scope?.loading || false,
      error: scope?.error,
      initialized: scope?.initialized || false,
      refresh,
   };
}
export function useSaveAnnotation() {
   const { dispatch } = useANNContext();
   return useCallback(async (input: SaveAnnotationInput) => {
      dispatch(annSaveAnnotationRequest());
      try {
         const record = await ANNManager.saveAnnotation(input);
         dispatch(annSaveAnnotationSuccess(record));
         return record;
      } catch (err: any) {
         dispatch(annSaveAnnotationFailure(err?.message || String(err)));
         throw err;
      }
   }, [dispatch]);
}
export function useDeleteAnnotation() {
   const { dispatch } = useANNContext();
   return useCallback(async (id: string, organizationId?: string) => {
      dispatch(annSaveAnnotationRequest());
      try {
         await ANNManager.deleteAnnotation(id, organizationId);
         dispatch(annDeleteAnnotationSuccess(id));
      } catch (err: any) {
         dispatch(annSaveAnnotationFailure(err?.message || String(err)));
         throw err;
      }
   }, [dispatch]);
}